import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import styled from "styled-components";
import { reactSelectStyling } from "../../../Components/Common/Forms/FormikInputHelper";
import { usePeriodicReportStore } from "../../../stores/PeriodicReport";
import { useSiteStore } from "../../../stores/Assets/site";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmailList = (value) =>
  value
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);

const PeriodicReportPage = () => {
  document.title = "Periodic Report Email - Clickorder Admin";

  const { isSending, sendPeriodicReport } = usePeriodicReportStore(
    (state) => state
  );
  const { getSites } = useSiteStore((state) => state);

  const [siteList, setSiteList] = useState([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(1);
    return d;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [timezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [toEmail, setToEmail] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        const list = await getSites();
        const formattedOptions = (list || []).map((site) => ({
          label: `${site.site_id} - ${site.name}`,
          value: site.site_id,
        }));
        setSiteList(formattedOptions);
      } catch (err) {
        console.error("Error fetching sites:", err);
        setFormError("Failed to load sites");
      } finally {
        setIsLoadingSites(false);
      }
    };
    fetchSites();
  }, [getSites]);

  const selectedSiteOptions = siteList.filter((site) =>
    selectedSiteIds.includes(site.value)
  );

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
    }
  };

  const handleSend = async () => {
    setFormError(null);

    if (!selectedSiteIds.length) {
      setFormError("Please select at least one site.");
      return;
    }
    if (!startDate || !endDate) {
      setFormError("Please select a date range.");
      return;
    }
    if (!toEmail || !EMAIL_PATTERN.test(toEmail.trim())) {
      setFormError("Please enter a valid email address.");
      return;
    }
    const ccEmails = parseEmailList(cc);
    const bccEmails = parseEmailList(bcc);
    const invalidCc = ccEmails.filter((email) => !EMAIL_PATTERN.test(email));
    const invalidBcc = bccEmails.filter((email) => !EMAIL_PATTERN.test(email));
    if (invalidCc.length) {
      setFormError(
        `Invalid CC address${invalidCc.length > 1 ? "es" : ""}: ${invalidCc.join(", ")}`
      );
      return;
    }
    if (invalidBcc.length) {
      setFormError(
        `Invalid BCC address${invalidBcc.length > 1 ? "es" : ""}: ${invalidBcc.join(", ")}`
      );
      return;
    }

    try {
      await sendPeriodicReport({
        siteIds: selectedSiteIds,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        toEmail: toEmail.trim(),
        cc: ccEmails,
        bcc: bccEmails,
        timezone,
      });
      toast.success("Report emailed successfully", {
        position: "bottom-right",
        autoClose: 2000,
        theme: "colored",
      });
    } catch (e) {
      setFormError(
        e?.response?.data?.message || "Failed to send the periodic report."
      );
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3 pb-1">
          <Col xs={12}>
            <div className="d-flex align-items-lg-center flex-lg-row flex-column">
              <div className="flex-grow-1">
                <h4 className="fs-16 mb-1">Periodic Report Email</h4>
                <p className="text-muted mb-0">
                  Manually email a site performance report for a selected date
                  range. ROOT only.
                </p>
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={8} xl={6}>
            <Card>
              <CardBody>
                {formError && (
                  <Alert color="danger" className="mb-3">
                    {formError}
                  </Alert>
                )}

                <FormGroup>
                  <Label>Sites</Label>
                  <StyledSelectWrapper>
                    <ReactSelect
                      {...reactSelectStyling}
                      options={siteList}
                      value={selectedSiteOptions}
                      placeholder={
                        isLoadingSites ? "Loading sites..." : "Select sites"
                      }
                      isSearchable
                      isClearable
                      isMulti
                      isLoading={isLoadingSites}
                      onChange={(selectedOptions) =>
                        setSelectedSiteIds(
                          selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : []
                        )
                      }
                    />
                  </StyledSelectWrapper>
                </FormGroup>

                <FormGroup>
                  <Label>Date range</Label>
                  <Flatpickr
                    options={{
                      mode: "range",
                      enableTime: true,
                      dateFormat: "Y-m-d H:i",
                      defaultDate: [startDate, endDate],
                    }}
                    className="form-control w-100 bg-white"
                    value={[startDate, endDate]}
                    onChange={handleDateChange}
                  />
                  <small className="text-muted">Timezone: {timezone}</small>
                </FormGroup>

                <FormGroup>
                  <Label>Recipient email</Label>
                  <Input
                    type="email"
                    placeholder="name@example.com"
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>CC</Label>
                  <Input
                    type="text"
                    placeholder="one@example.com, two@example.com"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                  />
                  <small className="text-muted">
                    Optional. Separate multiple addresses with commas.
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label>BCC</Label>
                  <Input
                    type="text"
                    placeholder="one@example.com, two@example.com"
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                  />
                  <small className="text-muted">
                    Optional. Separate multiple addresses with commas.
                  </small>
                </FormGroup>

                <Button
                  style={{ background: "#ce9558", borderColor: "#ce9558" }}
                  className="d-flex align-items-center gap-2"
                  disabled={isSending}
                  onClick={handleSend}
                >
                  {isSending ? (
                    <>
                      <Spinner size="sm" /> Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line"></i> Send report
                    </>
                  )}
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PeriodicReportPage;

const StyledSelectWrapper = styled.div`
  min-width: 200px;
`;
